import { isTen, rankOf, suitOf, type CardId, type Suit } from './cards';
import { legalActions, type PlayableAction } from './legal';
import type { SeatView } from './projection';
import { pick, type Rng } from './rng';
import { resolveTrick } from './trick';
import type { Play, TrumpState } from './types';
import { teamOf } from './types';

export type BotLevel = 'easy' | 'normal';

/**
 * Bots receive a SeatView only — their own hand plus public history — so they can never inspect
 * opponents' cards or the hidden reserve (PRD T13). Easy plays any legal move; normal follows simple,
 * explainable Mendikot heuristics: protect tens, feed tens to a winning partner, win tricks that hold tens.
 */
export function botAction(view: SeatView, rng: Rng, level: BotLevel = 'normal'): PlayableAction {
  const actions = legalActions(view);
  if (actions.length === 0) throw new Error(`Bot at seat ${view.seat} has no legal action`);
  if (level === 'easy') return pick(actions, rng);
  if (view.hand.phase === 'RESERVE')
    return { type: 'RESERVE_TRUMP', cardId: chooseReserve(view.myHand) };

  const cards = actions.flatMap((a) => (a.type === 'PLAY_CARD' ? [a.cardId] : []));
  const canRequestReveal = actions.some((a) => a.type === 'REQUEST_REVEAL');
  const trick = view.hand.trick;
  if (trick.length === 0) return playCard(chooseLead(view, cards));

  const winning = resolveTrick(
    trick,
    view.hand.trickIndex,
    view.hand.trump,
    view.rules.duplicateTie,
  );
  const myTeam = teamOf(view.seat);
  const partnerWinning = teamOf(winning.seat) === myTeam;
  const tensInTrick = trick.filter((p) => isTen(p.cardId)).length;
  const isLast = trick.length === view.rules.playerCount - 1;

  if (canRequestReveal && !partnerWinning && (tensInTrick > 0 || isLast)) {
    return { type: 'REQUEST_REVEAL' };
  }

  if (partnerWinning) {
    const partnerSafe = isLast || isStrong(winning, view);
    const tens = cards.filter(isTen);
    if (partnerSafe && tens.length > 0) return playCard(tens[0]!);
    return playCard(cheapest(cards));
  }

  const winners = cards.filter((c) => teamOf(simulatedWinner(view, c).seat) === myTeam);
  if (winners.length > 0 && (tensInTrick > 0 || isLast)) {
    const winningTen = winners.find(isTen);
    if (isLast && winningTen) return playCard(winningTen);
    return playCard(lowestRank(winners));
  }
  const ace = winners.find((c) => rankOf(c) === 14);
  if (ace) return playCard(ace);
  return playCard(cheapest(cards));
}

const playCard = (cardId: CardId): PlayableAction => ({ type: 'PLAY_CARD', cardId });

/** Reserve a low card from the longest suit of the first packet, keeping tens playable. */
function chooseReserve(hand: readonly CardId[]): CardId {
  const bySuit = groupBySuit(hand);
  const suits = [...bySuit.entries()].sort(
    (a, b) => b[1].length - a[1].length || sumRanks(b[1]) - sumRanks(a[1]),
  );
  const longest = suits[0]![1];
  const nonTens = longest.filter((c) => !isTen(c));
  return lowestRank(nonTens.length > 0 ? nonTens : longest);
}

function chooseLead(view: SeatView, cards: readonly CardId[]): CardId {
  const trumpSuit = knownTrump(view.hand.trump);
  const nonTrump = cards.filter((c) => suitOf(c) !== trumpSuit);
  const pool = nonTrump.length > 0 ? nonTrump : [...cards];
  const ace = pool.find((c) => rankOf(c) === 14);
  if (ace) return ace;
  const bySuit = groupBySuit(pool.filter((c) => !isTen(c)));
  const longest = [...bySuit.values()].sort((a, b) => b.length - a.length)[0];
  return longest ? lowestRank(longest) : cheapest(pool);
}

/** A card unlikely to be beaten: a trump, or an Ace/King of the led suit before anyone has cut. */
function isStrong(play: Play, view: SeatView): boolean {
  const trumpSuit = knownTrump(view.hand.trump);
  if (trumpSuit !== null && suitOf(play.cardId) === trumpSuit) return rankOf(play.cardId) >= 12;
  return rankOf(play.cardId) >= 13 && trumpSuit === null && view.hand.trump.status !== 'HIDDEN';
}

function knownTrump(trump: TrumpState): Suit | null {
  return trump.status === 'HIDDEN' ? null : trump.suit;
}

/** Winner if this seat played `cardId` now, including a Cut that would set trump. */
function simulatedWinner(view: SeatView, cardId: CardId): Play {
  const hand = view.hand;
  const order = hand.trick.length;
  const plays: Play[] = [...hand.trick, { seat: view.seat, cardId, order }];
  let trump = hand.trump;
  const led = suitOf(hand.trick[0]!.cardId);
  if (trump.mode === 'CUT' && trump.status === 'NONE' && suitOf(cardId) !== led) {
    trump = {
      ...trump,
      status: 'SET',
      suit: suitOf(cardId),
      cardId,
      activatedAt: { trick: hand.trickIndex, order },
    };
  }
  return resolveTrick(plays, hand.trickIndex, trump, view.rules.duplicateTie);
}

/** Lowest card, treating tens as precious so they are thrown away last. */
function cheapest(cards: readonly CardId[]): CardId {
  return [...cards].sort((a, b) => value(a) - value(b))[0]!;
}

function lowestRank(cards: readonly CardId[]): CardId {
  return [...cards].sort((a, b) => rankOf(a) - rankOf(b))[0]!;
}

const value = (c: CardId): number => (isTen(c) ? 100 : rankOf(c));

function groupBySuit(cards: readonly CardId[]): Map<Suit, CardId[]> {
  const map = new Map<Suit, CardId[]>();
  for (const c of cards) {
    const list = map.get(suitOf(c)) ?? [];
    list.push(c);
    map.set(suitOf(c), list);
  }
  return map;
}

const sumRanks = (cards: readonly CardId[]): number => cards.reduce((s, c) => s + rankOf(c), 0);
